import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import { LearningFacadeService } from '../../../services/learning-facade.service';
import {
  SkillDocumentResponse,
  SkillPracticeTaskResponse,
  SkillPracticeTaskStatus,
} from '../../../models/skill-learning.model';
import { SKILL_PRACTICE_TASK_STATUS } from '../../../options/skill-learning.option';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SkillDocumentPreviewModalComponent } from '../../shared/skill-document-preview-modal/skill-document-preview-modal.component';

type SkillDocumentInput = SkillDocumentResponse | null;
type SkillPracticeTaskInput = SkillPracticeTaskResponse | null;
type PracticeTaskType = 'PRACTICE' | 'TEST_REVIEW';

@Component({
  selector: 'app-practice-level-container',
  templateUrl: './practice-level-container.component.html',
  styleUrls: ['./practice-level-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticeLevelContainerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() specializationId: string;
  @Input() level: number;
  @Input() description: string;
  @Input() documentFileId: string | null = null;
  @Input() document: SkillDocumentInput = null;
  @Input() documentLevel: number | null = null;
  @Input() showLearningMaterial = true;
  @Input() isDisabled = false;
  @Input() taskType: PracticeTaskType = 'PRACTICE';
  @Input() titleKey = 'SKILL_LEARNING_PRACTICE_LEVEL_TITLE';
  @Input() descriptionTitleKey = 'SKILL_LEARNING_PRACTICE_LEVEL_DESCRIPTION';
  @Input() startButtonKey = 'SKILL_LEARNING_PRACTICE_START';
  @Input() startHintKey = 'SKILL_LEARNING_PRACTICE_START_HINT';
  @Input() lockedMessageKey = 'SKILL_LEARNING_PRACTICE_LOCKED';
  @Input() completedTitleKey = 'SKILL_LEARNING_PRACTICE_CONFIRMED_TITLE';
  @Input() completedTextKey = 'SKILL_LEARNING_PRACTICE_CONFIRMED_TEXT';
  @Input() initialTask: SkillPracticeTaskInput = null;
  @Input() loadTaskOnInit = true;

  @Output() completed = new EventEmitter<void>();

  taskStatus: SkillPracticeTaskStatus | null = null;
  task: SkillPracticeTaskInput = null;
  curatorName: string | null = null;
  curatorPhone: string | null = null;
  planfixTaskId: number | null = null;
  planfixTaskUrl: string | null = null;
  recreateAvailableAt: string | null = null;
  isLoading = false;

  private destroy$ = new Subject<void>();
  private pollingInterval: any;
  private contextSpecializationId: string | null = null;
  private contextLevel: number | null = null;
  private contextTaskType: PracticeTaskType | null = null;
  private isInitialized = false;

  constructor(
    private facade: LearningFacadeService,
    private cdr: ChangeDetectorRef,
    private modalService?: NgbModal,
  ) {}

  ngOnInit(): void {
    this.storeContextIdentity();
    this.isInitialized = true;
    this.loadInitialTaskStatus();
  }

  ngOnChanges(): void {
    if (!this.hasContextIdentityChanged()) {
      return;
    }
    this.destroy$.next();
    this.clearPolling();
    this.resetTask();
    this.isLoading = false;
    this.storeContextIdentity();
    if (this.isInitialized) {
      this.loadInitialTaskStatus();
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.isInitialized = false;
    this.clearPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canStart(): boolean {
    return this.isRestartableStatus && !this.isDisabled && !this.isLoading;
  }

  get canShowStartButton(): boolean {
    return !this.isLoading && !this.isCompleted && this.isRestartableStatus;
  }

  get isStartButtonDisabled(): boolean {
    return !this.canStart;
  }

  get isCompleted(): boolean {
    return this.taskStatus === SKILL_PRACTICE_TASK_STATUS.COMPLETED;
  }

  get canShowLockedMessage(): boolean {
    return this.isDisabled && !this.isCompleted && this.isRestartableStatus;
  }

  get normalizedDescription(): string {
    return (this.description || '').trim();
  }

  get shouldShowLearningMaterial(): boolean {
    return this.showLearningMaterial;
  }

  private get isRestartableStatus(): boolean {
    return !this.taskStatus || this.taskStatus === SKILL_PRACTICE_TASK_STATUS.CANCELLED;
  }

  get documentUrl(): string | null {
    if (this.hasDocument) {
      return this.facade.buildDocumentDownloadUrl(this.specializationId, this.documentLevel || this.level);
    }
    return null;
  }

  get hasDocument(): boolean {
    return !!this.document || !!this.documentFileId;
  }

  loadTaskStatus(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.facade.getPracticeTaskStatus(this.specializationId, this.apiLevel, this.taskType)
      .takeUntil(this.destroy$)
      .subscribe(
        task => {
          this.applyTask(task);
          this.isLoading = false;
          this.cdr.markForCheck();

          if (this.isActiveTask(task)) {
            this.startPolling();
          }
        },
        () => {
          this.resetTaskIfNoCurrentTask();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  onStartClicked(): void {
    if (!this.canStart) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.facade.startPracticeLevel(this.specializationId, this.apiLevel, this.taskType)
      .takeUntil(this.destroy$)
      .subscribe(
        task => {
          this.applyTask(task);
          this.isLoading = false;
          this.cdr.markForCheck();

          if (this.isActiveTask(task)) {
            this.startPolling();
          }
        },
        () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  onDownloadClicked(): void {
    const url = this.documentUrl;
    if (url) {
      window.open(url, '_blank');
    }
  }

  onPreviewClicked(): void {
    if (!this.document) {
      this.onDownloadClicked();
      return;
    }
    if (!this.modalService) {
      this.onDownloadClicked();
      return;
    }
    const modalRef = this.modalService.open(SkillDocumentPreviewModalComponent, { size: 'lg' });
    modalRef.componentInstance.specializationId = this.specializationId;
    modalRef.componentInstance.level = this.documentLevel || this.level;
    modalRef.componentInstance.document = this.document;
  }

  private loadInitialTaskStatus(): void {
    if (!this.specializationId || !this.level) {
      return;
    }

    if (this.initialTask) {
      this.applyTask(this.initialTask);
      if (this.isActiveTask(this.initialTask)) {
        this.startPolling();
      }
      this.cdr.markForCheck();
      return;
    }

    if (this.loadTaskOnInit && !this.isDisabled) {
      this.loadTaskStatus();
    }
  }

  private startPolling(): void {
    this.clearPolling();
    this.pollingInterval = setInterval(() => {
      this.facade.getPracticeTaskStatus(this.specializationId, this.apiLevel, this.taskType)
        .takeUntil(this.destroy$)
        .subscribe(
          task => {
            this.applyTask(task);
            if (task.status === SKILL_PRACTICE_TASK_STATUS.COMPLETED
              || task.status === SKILL_PRACTICE_TASK_STATUS.CANCELLED) {
              this.clearPolling();
              this.completed.emit();
            }
            this.cdr.markForCheck();
          },
          () => {
            this.clearPolling();
            this.cdr.markForCheck();
          },
        );
    }, 60000);
  }

  private clearPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private applyTask(task: SkillPracticeTaskResponse): void {
    this.task = task;
    this.taskStatus = task.status;
    this.curatorName = this.resolveCuratorName(task);
    this.curatorPhone = this.resolveCuratorPhone(task);
    this.planfixTaskId = task.planfixTaskId;
    this.planfixTaskUrl = task.planfixTaskUrl;
    this.recreateAvailableAt = task.recreateAvailableAt;
  }

  private isActiveTask(task: SkillPracticeTaskResponse): boolean {
    return task.status === SKILL_PRACTICE_TASK_STATUS.CREATED
      || task.status === SKILL_PRACTICE_TASK_STATUS.IN_WORK;
  }

  private resetTask(): void {
    this.task = null;
    this.taskStatus = null;
    this.curatorName = null;
    this.curatorPhone = null;
    this.planfixTaskId = null;
    this.planfixTaskUrl = null;
    this.recreateAvailableAt = null;
  }

  private resetTaskIfNoCurrentTask(): void {
    if (!this.taskStatus && !this.planfixTaskId && !this.planfixTaskUrl) {
      this.resetTask();
    }
  }

  private get apiLevel(): number {
    return this.taskType === 'TEST_REVIEW' ? 1 : this.level;
  }

  private resolveCuratorName(task: SkillPracticeTaskResponse): string | null {
    if (task.assignedCurator && task.assignedCurator.fullName) {
      return task.assignedCurator.fullName;
    }
    return null;
  }

  private resolveCuratorPhone(task: SkillPracticeTaskResponse): string | null {
    if (task.assignedCurator && task.assignedCurator.phone) {
      return task.assignedCurator.phone;
    }
    return null;
  }

  private hasContextIdentityChanged(): boolean {
    return this.contextSpecializationId !== this.specializationId
      || this.contextLevel !== this.level
      || this.contextTaskType !== this.taskType;
  }

  private storeContextIdentity(): void {
    this.contextSpecializationId = this.specializationId;
    this.contextLevel = this.level;
    this.contextTaskType = this.taskType;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/practice-level/practice-level-container.component.ts